import React, { useEffect, useState } from 'react';
import { Mongo } from 'meteor/mongo';
import { Docmnt } from '/imports/types';
import { strings } from '/imports/strings';
import { Select } from '/imports/ui/Select';

interface ActiveFilterProps {
  onChange?: (value: string) => void;
  onQuery?: (query?: { active: boolean | { $ne: true } }) => void;
}

/**
 * Builds an active/inactive/all filter: a Select component plus a predicate, syncing the chosen state into a Mongo query.
 * @param query - Current query whose `active` clause is read and mutated.
 * @param setQuery - Setter to apply the updated query.
 * @param otherQuery - Extra query clauses merged in on each change.
 * @returns Object exposing the filter `Component`, current value, and `filter()` predicate accessor.
 */
export const createActiveFilter = <T extends Docmnt>(
  query?: Mongo.Query<T>,
  setQuery?: React.Dispatch<React.SetStateAction<Mongo.Query<T>>>,
  otherQuery?: Mongo.Query<T>,
) => {
  const f = {
    filter: (d: T) => !!d.active,
  };
  const q = query as any;
  const F = {
    currentValue: q === undefined ? 'active' : q.active === true ? 'active' : q.active !== undefined ? 'all' : 'inactive',
    /** Select that drives the active/inactive/all choice and propagates query changes. */
    Component: (props: ActiveFilterProps) => {
      const [activeFilter, setActiveFilter] = useState(F.currentValue);
      f.filter = (d: T) => {
        if (activeFilter === 'active') return !!d.active;
        if (activeFilter === 'inactive') return !d.active;
        return true;
      };
      useEffect(() => {
        if (activeFilter === F.currentValue) return;
        F.currentValue = activeFilter;
        if (activeFilter === 'active') {
          const q = { active: true };
          query && setQuery?.({ ...query, ...q, ...otherQuery });
          props.onQuery?.(q);
        }
        else if (activeFilter === 'inactive') {
          const q = { active: { $ne: true } };
          query && setQuery?.({ ...query, ...q, ...otherQuery });
          props.onQuery?.(q as any);
        }
        else if (query) {
          delete (query as any).active;
          const q = { ...query, ...otherQuery };
          setQuery?.(q);
          props.onQuery?.(q as any);
        }
        else {
          props.onQuery?.({} as any);
        }
        props.onChange?.(activeFilter);
      }, [activeFilter]);
      return (
        <Select
          className="filter"
          value={F.currentValue}
          options={[
            { value: 'active', label: strings.Active() },
            { value: 'inactive', label: strings.Inactive() },
            { value: 'all', label: strings.All() },
          ]}
          onChange={(activeFilter) => setActiveFilter(activeFilter)}
        />
      );
    },
    /** Current predicate reflecting the selected filter. */
    filter() {
      return f.filter;
    }
  };
  return F;
};